import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import {
    VStack,
    HStack,
    Text,
    Button,
    IconButton,
    Box,
    Image,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
} from '@chakra-ui/react';
import { Plus, Trash2, RefreshCw } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter } from './ManageDialogFrame';
import { useImageLibraryStore } from '../../state/useImageLibraryStore';
import { sharedLinkManager } from '../../core/LinkManager';

const ACCEPTED_TYPES = 'image/png,image/jpeg,image/bmp,image/tiff,image/gif,image/webp,image/svg+xml';

export function ManageImagesDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'images');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const images = useImageLibraryStore((state) => state.images);
    const addImage = useImageLibraryStore((state) => state.addImage);
    const removeImage = useImageLibraryStore((state) => state.removeImage);
    const reloadImage = useImageLibraryStore((state) =>state.reloadImage); const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFilesChosen = (files: FileList | null) => {
        if (!files) return;
        Array.from(files).forEach((file) => {
            const url = URL.createObjectURL(file);
            const img = new window.Image();
            img.onload = () => {
                addImage({
                    name: file.name,
                    url,
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                    format: (file.type.split('/')[1]?.toUpperCase() ?? 'PNG') as never,
                    usedIn: [],
                });
            };
            img.onerror = () => {
                addImage({
                    name: file.name,
                    url,
                    width: 0,
                    height: 0,
                    format: (file.type.split('/')[1]?.toUpperCase() ?? 'PNG') as never,
                    usedIn: [],
                });
            };
            img.src = url;

            // Keep the file linked in the shared LinkManager registry (Manage
            // Images doubles as an image-linking entry point).
            sharedLinkManager.linkFile('Image', file.name, { positioning: 'OriginToOrigin' });
        });
    };

    const handleAddClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <ManageDialogFrame
            title={t('manage.manageImages')}
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            footer={
                <ManageDialogCloseFooter
                    onClose={onClose}
                    extra={
                        <Button
                            size="sm"
                            leftIcon={<Plus size={14} />}
                            variant="ghost"
                            onClick={handleAddClick}
                        >{t('manage.addImage')}</Button>
                    }
                />
            }
        >
            <VStack spacing={3} align="stretch">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    multiple
                    className="hidden-file-input"
                    onChange={(e) => {
                        handleFilesChosen(e.target.files);
                        e.target.value = '';
                    }}
                />

                {images.length === 0 ? (
                    <Text fontSize="sm" color="var(--color-text-secondary)">{t('manage.noImagesInTheProjectLibraryYetAddImagesToUseThemInViewsAndSheets')}</Text>
                ) : (
                    <Box overflowX="auto" maxH="52vh">
                        <Table size="sm" variant="unstyled">
                            <Thead position="sticky" top={0} bg="var(--color-bg-secondary)" zIndex={1}>
                                <Tr>
                                    <Th color="var(--color-text-secondary)" fontSize="xs">{t('manage.preview')}</Th>
                                    <Th color="var(--color-text-secondary)" fontSize="xs">{t('manage.name')}</Th>
                                    <Th color="var(--color-text-secondary)" fontSize="xs">{t('manage.size')}</Th>
                                    <Th color="var(--color-text-secondary)" fontSize="xs">{t('manage.format')}</Th>
                                    <Th color="var(--color-text-secondary)" fontSize="xs">{t('manage.usedIn')}</Th>
                                    <Th color="var(--color-text-secondary)" fontSize="xs" width="80px" />
                                </Tr>
                            </Thead>
                            <Tbody>
                                {images.map((image) => (
                                    <Tr key={image.id}>
                                        <Td borderColor="var(--color-border)">
                                            <Image
                                                src={image.url}
                                                alt={image.name}
                                                boxSize="36px"
                                                objectFit="cover"
                                                borderRadius="var(--cad-radius-sm)"
                                                border="1px solid var(--color-border)"
                                            />
                                        </Td>
                                        <Td fontSize="sm" borderColor="var(--color-border)" whiteSpace="nowrap">
                                            {image.name}
                                        </Td>
                                        <Td fontSize="sm" borderColor="var(--color-border)" whiteSpace="nowrap">
                                            {image.width} × {image.height}
                                        </Td>
                                        <Td fontSize="sm" borderColor="var(--color-border)">{image.format}</Td>
                                        <Td fontSize="sm" borderColor="var(--color-border)">
                                            {image.usedIn.length > 0 ? `${image.usedIn.length} element(s)` : '—'}
                                        </Td>
                                        <Td borderColor="var(--color-border)">
                                            <HStack spacing={1}>
                                                <IconButton
                                                    aria-label={`Reload ${image.name}`}
                                                    icon={<RefreshCw size={12} />}
                                                    size="xs"
                                                    variant="ghost"
                                                    onClick={() => reloadImage(image.id)}
                                                />
                                                <IconButton
                                                    aria-label={`Delete ${image.name}`}
                                                    icon={<Trash2 size={12} />}
                                                    size="xs"
                                                    variant="ghost"
                                                    isDisabled={image.usedIn.length > 0}
                                                    onClick={() => removeImage(image.id)}
                                                />
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                )}
            </VStack>
        </ManageDialogFrame>
    );
}
